export const T12BestPractices = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Expert Guidance</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            T12 Best Practices for Maximum Sale Price
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Follow these proven strategies to present your property in the best possible light and command top dollar from buyers.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/d4dc9d3bcbe2c8e421b8517a46dc16b1.jpg"
                alt="Professional implementing T12 best practices for apartment building sale"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6 border border-teal-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">The Golden Rules of T12 Preparation</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-teal-600 font-bold"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Start Early</div>
                    <p className="text-sm text-gray-700">Begin maintaining clean T12 records 12-18 months before you plan to sell. This gives you time to optimize performance and address issues.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-teal-600 font-bold"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Be Consistent</div>
                    <p className="text-sm text-gray-700">Use the same accounting methods, categories, and formats every month. Consistency builds credibility and makes analysis easier.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-teal-600 font-bold"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Document Everything</div>
                    <p className="text-sm text-gray-700">Keep receipts, invoices, bank statements, and supporting documentation for every line item in your T12.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-teal-600 font-bold"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Be Transparent</div>
                    <p className="text-sm text-gray-700">Disclose issues upfront with explanations. Buyers respect honesty and will work with you to find solutions.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-teal-600 font-bold"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Get Professional Help</div>
                    <p className="text-sm text-gray-700">Have your accountant review T12 before listing. Professional presentation adds credibility and catches errors.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-calendar-check-line text-3xl text-teal-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Monthly Maintenance</h3>
            <p className="text-gray-700 mb-4">
              Don't wait until you're ready to sell to compile T12. Maintain accurate monthly records throughout ownership.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Record all income and expenses monthly</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Reconcile to bank statements each month</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>File supporting documents immediately</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                <span>Review for accuracy before closing books</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-file-chart-line text-3xl text-blue-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Professional Formatting</h3>
            <p className="text-gray-700 mb-4">
              Present your T12 in a clean, professional format that's easy for buyers to analyze and verify.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                <span>Use standard income statement format</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                <span>Include month-by-month breakdown</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                <span>Show annual totals and averages</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-blue-600 mt-0.5"></i>
                <span>Clearly label all categories</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-shield-check-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Verification Ready</h3>
            <p className="text-gray-700 mb-4">
              Anticipate buyer due diligence and have all verification documents organized and accessible.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                <span>Bank statements match T12 income</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                <span>Receipts for all major expenses</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                <span>Tax returns align with T12</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-checkbox-circle-fill text-green-600 mt-0.5"></i>
                <span>Rent roll matches rental income</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-8 border border-gray-200 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">T12 Presentation Checklist</h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-file-list-3-line text-teal-600 text-xl"></i>
                Before Listing
              </h4>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">T12 covers most recent complete 12 months</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">All income is actual collected, not scheduled</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Vacancy loss accurately calculated and shown</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Operating expenses complete and accurate</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Management fees included (even if self-managed)</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">No capital expenditures in operating expenses</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">No personal expenses included</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Consistent accounting methods throughout</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Professional formatting and presentation</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Accountant has reviewed for accuracy</span>
                </label>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-folder-open-line text-teal-600 text-xl"></i>
                Supporting Documentation Ready
              </h4>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">12 months of bank statements organized</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">3 years of tax returns with Schedule E</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Current rent roll with lease dates</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">All lease agreements accessible</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Property tax statements</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Insurance policies and costs</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Utility bills for 12 months</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Major expense receipts and invoices</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Service contracts and vendor information</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" className="w-5 h-5 text-teal-600 mt-0.5" />
                  <span className="text-gray-700">Capital expenditure history separate from T12</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-2xl p-8 text-white">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">The T12 Excellence Formula</h3>
            <p className="text-lg opacity-90 max-w-3xl mx-auto">
              Properties with excellent T12 documentation sell 20-30% faster and command 10-15% higher prices than comparable properties with poor financial records.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-3xl font-bold mb-2">Accurate</div>
              <div className="text-sm opacity-90">Every number verified and documented</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-3xl font-bold mb-2">Complete</div>
              <div className="text-sm opacity-90">All 12 months with supporting docs</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-3xl font-bold mb-2">Consistent</div>
              <div className="text-sm opacity-90">Same methods and categories throughout</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <div className="text-3xl font-bold mb-2">Professional</div>
              <div className="text-sm opacity-90">Clean presentation builds confidence</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};